import {
  HHAlert,
  HHButton,
  HHDialog,
  HHDialogActions,
  HHDialogContent,
  HHDialogTitle,
  HHTypography,
} from '@hinge-health/react-component-library';
import { ControlAction } from '../domain/enums';

export const CONTROL_ACTION_DIALOG_TEST_ID =
  'control-action-confirmation-dialog';
export const CONTROL_ACTION_DIALOG_CONFIRM_TEST_ID =
  'control-action-confirmation-dialog-confirm-button';
export const CONTROL_ACTION_DIALOG_CANCEL_TEST_ID =
  'control-action-confirmation-dialog-cancel-button';
export const CONTROL_ACTION_DIALOG_ERROR_TEST_ID =
  'control-action-confirmation-dialog-error-alert';

interface ActionCopy {
  title: string;
  message: string;
  confirmLabel: string;
}

const ACTION_COPY: Record<ControlAction, ActionCopy> = {
  [ControlAction.Pause]: {
    title: 'Pause ingestion?',
    message:
      'This will pause processing. It can be resumed from where it left off.',
    confirmLabel: 'Pause',
  },
  [ControlAction.Resume]: {
    title: 'Resume ingestion?',
    message: 'This will resume processing from where it was paused.',
    confirmLabel: 'Resume',
  },
  [ControlAction.Cancel]: {
    title: 'Cancel ingestion?',
    message:
      'This will permanently stop processing this file. This cannot be undone.',
    confirmLabel: 'Cancel Ingestion',
  },
};

interface ControlActionConfirmationDialogProps {
  open: boolean;
  action: ControlAction;
  fileName: string;
  loading?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ControlActionConfirmationDialog = ({
  open,
  action,
  fileName,
  loading = false,
  error = null,
  onConfirm,
  onCancel,
}: ControlActionConfirmationDialogProps): JSX.Element => {
  const { title, message, confirmLabel } = ACTION_COPY[action];

  return (
    <HHDialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      data-testid={CONTROL_ACTION_DIALOG_TEST_ID}
    >
      <HHDialogTitle>{title}</HHDialogTitle>
      <HHDialogContent>
        <HHTypography hhVariant="body1" sx={{ marginBottom: 2 }}>
          {message}
        </HHTypography>
        <HHTypography hhVariant="body2" color="textSecondary">
          File: <strong>{fileName}</strong>
        </HHTypography>
        {error && (
          <HHAlert
            hhVariant="variant-bypass"
            variant="outlined"
            severity="error"
            data-testid={CONTROL_ACTION_DIALOG_ERROR_TEST_ID}
            sx={{ marginTop: 2 }}
          >
            {error}
          </HHAlert>
        )}
      </HHDialogContent>
      <HHDialogActions>
        <HHButton
          hhVariant="variant-bypass"
          variant="outlined"
          data-testid={CONTROL_ACTION_DIALOG_CANCEL_TEST_ID}
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </HHButton>
        <HHButton
          hhVariant="variant-bypass"
          variant="contained"
          color={action === ControlAction.Cancel ? 'error' : 'primary'}
          data-testid={CONTROL_ACTION_DIALOG_CONFIRM_TEST_ID}
          onClick={onConfirm}
          disabled={loading}
        >
          {loading ? 'Working…' : confirmLabel}
        </HHButton>
      </HHDialogActions>
    </HHDialog>
  );
};
